/**
 * Customer Journey — Triple-Whale-style timeline of a single visitor's
 * touchpoints across multiple sessions, devices, and Shopify orders.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Eye,
  ShoppingCart,
  CreditCard,
  Package,
  FileText,
  Globe,
  Search,
  Loader2,
  ArrowRight,
  RotateCcw,
} from "lucide-react";

interface Touchpoint {
  source: "tracking_event" | "shopify_order" | "session_start";
  timestamp: string;
  type: string;
  pageUrl?: string;
  productTitle?: string;
  revenue?: number;
  currency?: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  metadata?: Record<string, unknown>;
}

interface JourneyResponse {
  email?: string;
  sessionId?: string;
  identifiedEmail?: string | null;
  sessionIds?: string[];
  timeline: Touchpoint[];
}

const ICONS: Record<string, JSX.Element> = {
  page_view: <Globe className="w-4 h-4" />,
  view_product: <Eye className="w-4 h-4" />,
  add_to_cart: <ShoppingCart className="w-4 h-4" />,
  begin_checkout: <CreditCard className="w-4 h-4" />,
  purchase: <Package className="w-4 h-4" />,
  refund: <RotateCcw className="w-4 h-4" />,
  session_start: <ArrowRight className="w-4 h-4" />,
  line_item: <Package className="w-4 h-4 opacity-60" />,
};

function iconFor(t: Touchpoint): JSX.Element {
  if (t.source === "shopify_order" && t.type.startsWith("order")) return ICONS.purchase;
  if (t.source === "shopify_order" && t.type === "line_item") return ICONS.line_item;
  return ICONS[t.type] ?? <FileText className="w-4 h-4" />;
}

function colorFor(t: Touchpoint): string {
  if (t.type === "purchase" || (t.source === "shopify_order" && t.type.startsWith("order")))
    return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
  if (t.type === "refund") return "bg-red-500/15 text-red-400 border-red-500/30";
  if (t.type === "add_to_cart" || t.type === "begin_checkout")
    return "bg-amber-500/15 text-amber-400 border-amber-500/30";
  if (t.type === "view_product") return "bg-sky-500/15 text-sky-400 border-sky-500/30";
  if (t.type === "session_start") return "bg-purple-500/15 text-purple-400 border-purple-500/30";
  return "bg-white/[0.04] text-white/60 border-white/[0.08]";
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pl-PL");
  } catch {
    return iso;
  }
}

function fmtMoney(v: number, currency = "PLN"): string {
  try {
    return new Intl.NumberFormat("pl-PL", { style: "currency", currency }).format(v);
  } catch {
    return `${v.toFixed(2)} ${currency}`;
  }
}

export default function CustomerJourneyPage() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<{ kind: "email" | "session"; value: string } | null>(null);

  const { data, isLoading, error } = useQuery<JourneyResponse>({
    queryKey: ["/api/customer-journey", active?.kind, active?.value],
    queryFn: () => {
      if (!active) throw new Error("no query");
      const path =
        active.kind === "email"
          ? `/api/customer-journey/by-email/${encodeURIComponent(active.value)}`
          : `/api/customer-journey/by-session/${encodeURIComponent(active.value)}`;
      return apiRequest("GET", path);
    },
    enabled: !!active,
  });

  const submit = () => {
    const v = query.trim();
    if (!v) return;
    setActive({ kind: v.includes("@") ? "email" : "session", value: v });
  };

  const totalRevenue = (data?.timeline || [])
    .filter((t) => t.source === "shopify_order" && t.type.startsWith("order"))
    .reduce((s, t) => s + (t.revenue || 0), 0);

  const conversionCount = (data?.timeline || []).filter(
    (t) => t.type === "purchase" || (t.source === "shopify_order" && t.type.startsWith("order"))
  ).length;

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Customer Journey</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pełna ścieżka klienta — od pierwszego kliknięcia w reklamę do zakupu i refundu. Wpisz
          email lub session ID.
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder="email@klient.pl  lub  session-abc123…"
          className="flex-1"
        />
        <Button onClick={submit}>
          <Search className="w-4 h-4 mr-1" /> Szukaj
        </Button>
      </div>

      {!active ? (
        <div className="border border-dashed rounded-lg p-12 text-center text-muted-foreground">
          Wpisz identyfikator klienta żeby zobaczyć jego ścieżkę.
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie ścieżki…
        </div>
      ) : error ? (
        <div className="text-red-400 text-sm">
          Błąd ładowania: {String((error as Error).message)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Touchpointy</div>
              <div className="text-2xl font-bold mt-1">{data?.timeline.length ?? 0}</div>
            </div>
            <div className="border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Zakupy</div>
              <div className="text-2xl font-bold mt-1">{conversionCount}</div>
            </div>
            <div className="border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Łączny przychód</div>
              <div className="text-2xl font-bold mt-1 text-emerald-400">
                {fmtMoney(totalRevenue)}
              </div>
            </div>
          </div>

          {data?.identifiedEmail && (
            <div className="text-xs text-muted-foreground mb-3">
              Zidentyfikowany email:{" "}
              <code className="bg-white/[0.05] px-2 py-0.5 rounded">{data.identifiedEmail}</code>
            </div>
          )}

          <div className="relative pl-6">
            <div className="absolute left-2 top-2 bottom-2 w-px bg-white/[0.08]" />
            {(data?.timeline ?? []).map((t, i) => (
              <div key={i} className="relative mb-4 pl-4">
                <div
                  className={`absolute -left-[18px] top-1 w-7 h-7 rounded-full border flex items-center justify-center ${colorFor(t)}`}
                >
                  {iconFor(t)}
                </div>
                <div className="border rounded-lg p-3 bg-white/[0.02]">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{t.type}</span>
                      {t.productTitle && (
                        <span className="text-xs text-muted-foreground">— {t.productTitle}</span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{fmtTime(t.timestamp)}</span>
                  </div>
                  {t.pageUrl && (
                    <div className="text-xs text-sky-400/80 mt-1 truncate">{t.pageUrl}</div>
                  )}
                  {(t.utmSource || t.utmMedium || t.utmCampaign || t.fbclid || t.gclid) && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {t.utmSource && <Badge label="src" value={t.utmSource} />}
                      {t.utmMedium && <Badge label="med" value={t.utmMedium} />}
                      {t.utmCampaign && <Badge label="cmp" value={t.utmCampaign} />}
                      {t.fbclid && <Badge label="fbclid" value="✓" tone="meta" />}
                      {t.gclid && <Badge label="gclid" value="✓" tone="google" />}
                    </div>
                  )}
                  {typeof t.revenue === "number" && t.revenue !== 0 && (
                    <div
                      className={`text-sm font-semibold mt-2 ${t.revenue > 0 ? "text-emerald-400" : "text-red-400"}`}
                    >
                      {fmtMoney(t.revenue, t.currency)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(data?.timeline ?? []).length === 0 && (
              <div className="text-sm text-muted-foreground py-8">
                Brak touchpointów dla tego identyfikatora.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Badge({ label, value, tone }: { label: string; value: string; tone?: "meta" | "google" }) {
  const cls =
    tone === "meta"
      ? "bg-blue-500/10 text-blue-300 border-blue-500/30"
      : tone === "google"
        ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
        : "bg-white/[0.04] text-white/60 border-white/[0.08]";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded border ${cls}`}>
      <span className="opacity-60 mr-1">{label}</span>
      {value}
    </span>
  );
}
